import React, { useState } from "react";
import {
    Box, Typography, Paper, TextField, Button, MenuItem, Select, FormControl, InputLabel, Breadcrumbs, Link,
    Snackbar, Alert, Checkbox, FormControlLabel, Dialog, DialogActions, DialogContent, DialogTitle
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { SelectChangeEvent } from "@mui/material/Select";
import { useNotificationManager } from '../../hooks/useNotificationHook';
import { wait } from "@testing-library/user-event/dist/utils";

// Define a type for notification
interface Notification {
    title: string;
    description: string;
    createdAt?: string;
    typeId: number;
    sendBulkEmails: boolean;
}

const PublishNewsUpdates: React.FC = () => {
    const navigate = useNavigate();
    const { notificationTypes, isLoading, isError, createNotification, isCreating } = useNotificationManager(localStorage.getItem("is_user_logged_in") === "true");

    const [errorMessage, setErrorMessage] = useState<string>("");
    const [successMessage, setSuccessMessage] = useState<string>("");
    const [notification, setNotification] = useState<Notification>({
        title: "",
        description: "",
        typeId: 1,
        sendBulkEmails: false, // Add this state to track the bulk email option
    });

    // Snackbar states
    const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
    const [openSuccessSnackbar, setOpenSuccessSnackbar] = useState(false);

    // Dialog state
    const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);

    // Handle category change in the form
    const handleCategoryChange = (event: SelectChangeEvent<string>) => {
        const selectedCategoryId = notificationTypes?.find((type: any) => type.name === event.target.value)?.id || 1;
        setNotification({
            ...notification,
            typeId: selectedCategoryId,  // Update typeId based on selected category
        });
    };

    // Handle form input change
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNotification({
            ...notification,
            [event.target.name]: event.target.value,
        });
    };

    // Handle bulk email toggle
    const handleBulkEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNotification({
            ...notification,
            sendBulkEmails: event.target.checked, // Set sendBulkEmails based on checkbox state
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!notification.typeId || !notification.title || !notification.description) {
            setErrorMessage("Please fill in all required fields.");
            setOpenErrorSnackbar(true);
            return;
        }

        // If the user wants to send bulk emails, show the confirmation dialog
        if (notification.sendBulkEmails) {
            setOpenConfirmationDialog(true);
        } else {
            // Proceed without confirmation if bulk emails are not selected
            try {

                const onSuccess = () => {
                    setSuccessMessage("Success! Your notification has been published.");
                    setOpenSuccessSnackbar(true);

                    //Reset form after successful notification submission
                    setNotification({
                        title: "",
                        description: "",
                        typeId: 1,
                        sendBulkEmails: false,
                    });
                };

                const onError = () => {
                    setErrorMessage("Oops! Something went wrong. Please try again.");
                    setOpenErrorSnackbar(true);
                };

                createNotification(notification, {onSuccess, onError} );
            } catch (error) {
                setErrorMessage("Oops! Something went wrong. Please try again.");
                setOpenErrorSnackbar(true);
            }
        }
    };

    const handleConfirmationDialogClose = async (confirmed: boolean) => {
        if (confirmed) {
            // Proceed with submitting the notification after confirmation
            try {

                const onSuccess = () => {
                    setSuccessMessage("Success! Your notification has been published.");
                    setOpenSuccessSnackbar(true);

                    // Reset form after successful notification submission
                    setNotification({
                        title: "",
                        description: "",
                        typeId: 1,
                        sendBulkEmails: false,
                    });
                };

                const onError = () => {
                    setErrorMessage("Oops! Something went wrong. Please try again.");
                    setOpenErrorSnackbar(true);
                };

                createNotification(notification, {onSuccess, onError} );

            } catch (error) {
                setErrorMessage("Oops! Something went wrong. Please try again.");
                setOpenErrorSnackbar(true);
            }
        }
        setOpenConfirmationDialog(false);
    };

    const renderCategories = () => {
        if (isLoading) {
            return <MenuItem value=""><em>Loading...</em></MenuItem>;
        }

        if (isError) {
            return <MenuItem value=""><em>Error loading categories</em></MenuItem>;
        }

        return notificationTypes?.map((type: any) => (
          <MenuItem key={type.id} value={type.name}>
              {type.name}
          </MenuItem>
        )) || <MenuItem value=""><em>No categories available</em></MenuItem>;
    };

    return (
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: "#f9f9f9", p: 3 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
              <Link underline="hover" color="inherit" onClick={() => navigate("/")} sx={{ cursor: "pointer" }}>
                  Home
              </Link>
              <Link underline="hover" color="inherit" onClick={() => navigate("/admin")} sx={{ cursor: "pointer" }}>
                  Admin
              </Link>
              <Link underline="hover" color="inherit" onClick={() => navigate("/about/news-updates")} sx={{ cursor: "pointer" }}>
                  News & Updates
              </Link>
              <Typography color="text.primary">Publish</Typography>
          </Breadcrumbs>

          {/* Main Content */}
          <Box sx={{ flex: 1, backgroundColor: "white", borderRadius: 2, boxShadow: 3, p: 3 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom color="primary.main">
                  Publish New Notification
              </Typography>
              <Paper elevation={2} sx={{ p: 3 }}>
                  <Typography fontWeight="light" color="primary.main" gutterBottom>
                      Please use the form below to create new notifications.
                  </Typography>
                  <form onSubmit={handleSubmit}>
                      <TextField
                        label="Title"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        name="title"
                        value={notification.title}
                        onChange={handleInputChange}
                        required
                      />
                      <TextField
                        label="Description"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        name="description"
                        value={notification.description}
                        onChange={handleInputChange}
                        required
                        multiline
                        rows={4}
                      />
                      <FormControl fullWidth margin="normal">
                          <InputLabel>Category</InputLabel>
                          <Select
                            label="Category"
                            value={notificationTypes?.find(type => type.id === notification.typeId)?.name || ""}
                            onChange={handleCategoryChange}
                            required
                          >
                              {renderCategories()}
                          </Select>
                      </FormControl>

                      {/* Bulk Email Toggle */}
                      <FormControlLabel
                        control={
                            <Checkbox
                              checked={notification.sendBulkEmails}
                              onChange={handleBulkEmailChange}
                              name="sendBulkEmails"
                            />
                        }
                        label="Send bulk emails to notify users"
                        sx={{ mt: 2 }}
                      />

                      <Button type="submit" variant="contained" color="primary" sx={{ mt: 2 }}>
                          {isCreating ? "Publishing..." : "Publish Notification"}
                      </Button>
                  </form>
              </Paper>

              <Snackbar
                open={openErrorSnackbar}
                autoHideDuration={6000}
                onClose={() => setOpenErrorSnackbar(false)}
                anchorOrigin={{
                    vertical: 'top', // Vertical position
                    horizontal: 'right', // Horizontal position
                }}
              >
                  <Alert
                    onClose={() => setOpenErrorSnackbar(false)}
                    severity="error"
                    sx={{ width: '100%' }}
                  >
                      {errorMessage}
                  </Alert>
              </Snackbar>

              <Snackbar
                open={openSuccessSnackbar}
                autoHideDuration={6000}
                onClose={() => setOpenSuccessSnackbar(false)}
                anchorOrigin={{
                    vertical: 'top', // Vertical position
                    horizontal: 'right', // Horizontal position
                }}
              >
                  <Alert
                    onClose={() => setOpenSuccessSnackbar(false)}
                    severity="success"
                    sx={{ width: '100%' }}
                  >
                      {successMessage}
                  </Alert>
              </Snackbar>

              {/* Confirmation Dialog */}
              <Dialog
                open={openConfirmationDialog}
                onClose={() => setOpenConfirmationDialog(false)}
              >
                  <DialogTitle>Confirm Bulk Email Notification</DialogTitle>
                  <DialogContent>
                      <Typography>
                          Are you sure you want to send bulk emails to notify users about this update?
                      </Typography>
                  </DialogContent>
                  <DialogActions>
                      <Button onClick={() => handleConfirmationDialogClose(false)} color="primary">
                          Cancel
                      </Button>
                      <Button onClick={() => handleConfirmationDialogClose(true)} color="primary">
                          Confirm
                      </Button>
                  </DialogActions>
              </Dialog>

          </Box>
      </Box>
    );
};

export default PublishNewsUpdates;
